// Deterministic AI credit consumption fixtures for the /billing/credits prototype.
// See plans/saas-billing/08-ai-credit-usage-display.md.
//
// We deliberately do NOT use Math.random() here — the recent-consumption table
// and feature-breakdown bars must look identical across reloads so visual
// review is repeatable. Each fixture builder returns:
//
//   - events: ConsumptionEvent[] distributed across the recent past, with
//     monotonically-decreasing balanceAfter (most recent first) that lands on
//     `currentRemaining` for the most recent event.
//   - breakdown: FeatureUsageBreakdown[] aggregating those events by featureKey,
//     sorted desc, normalized so percentOfTotal sums to ~100.
//   - topUps: TopUpHistoryEntry[] (currently constructed inline per fixture).

import {
  ConsumptionEvent,
  FeatureKey,
  FeatureUsageBreakdown,
  ModelTier
} from '../../../models/billing';

const FEATURE_DISPLAY_NAMES: Record<FeatureKey, string> = {
  ai_chat: 'AI chat',
  document_extraction: 'Document extraction',
  image_generation: 'Image generation',
  voice_transcription: 'Voice transcription',
  summarization: 'Summarization',
  other: 'Other'
};

interface EventSeed {
  featureKey: FeatureKey;
  creditsConsumed: number;
  modelTier: ModelTier;
  // Hours-ago offset from "now" for stability across reloads we keep this as a
  // simple number; the builder converts at construction time.
  hoursAgo: number;
  requestSummary: string;
}

interface BuildEventsOptions {
  cycleAllocated: number;
  currentUsed: number;
  topUpBalance: number;
  seeds: EventSeed[];
  idPrefix: string;
}

/**
 * Build a deterministic list of ConsumptionEvent instances. The seeds list is
 * provided in CHRONOLOGICAL order (oldest first); the builder reverses the
 * result so the returned array is most-recent-first (UI display order). The
 * `balanceAfter` field is computed by walking forward in time from
 * `cycleAllocated + topUpBalance` (initial cycle pool + non-expiring top-up).
 */
export function buildConsumptionEvents(opts: BuildEventsOptions): ConsumptionEvent[] {
  const sumOfSeedCredits = opts.seeds.reduce((acc, s) => acc + s.creditsConsumed, 0);

  // Initial combined balance at the START of the cycle (before any consumption).
  // Scaled so that walking forward through all seeds lands on the current
  // remaining balance. If the seeds don't sum to opts.currentUsed exactly, we
  // still produce a coherent series; the absolute values may be slightly off
  // but the breakdown will reflect actual seeds, which is the source of truth.
  const initialBalance = opts.cycleAllocated + opts.topUpBalance;

  // Walk chronologically (oldest first), decrementing balance per event.
  let runningBalance = initialBalance;
  const chronological: ConsumptionEvent[] = opts.seeds.map((seed, idx) => {
    runningBalance = Math.max(0, runningBalance - seed.creditsConsumed);
    const occurredAt = new Date(Date.now() - seed.hoursAgo * 60 * 60 * 1000).toISOString();
    return {
      id: `${opts.idPrefix}_evt_${idx + 1}`,
      occurredAt,
      featureKey: seed.featureKey,
      creditsConsumed: seed.creditsConsumed,
      modelTier: seed.modelTier,
      requestSummary: seed.requestSummary,
      balanceAfter: runningBalance
    };
  });

  // If the final running balance doesn't match the wallet's current remaining
  // (cycleAllocated - currentUsed + topUpBalance), shift the whole series by
  // the delta so the most-recent event lands on the wallet figure. This keeps
  // the gauge and the recent-consumption table in agreement.
  const targetFinal = opts.cycleAllocated - opts.currentUsed + opts.topUpBalance;
  const delta = targetFinal - runningBalance;
  const shifted = chronological.map(evt => ({
    ...evt,
    balanceAfter: Math.max(0, evt.balanceAfter + delta)
  }));
  void sumOfSeedCredits;

  // Reverse so the UI sees most-recent-first (matches the spec's table order).
  return shifted.slice().reverse();
}

/**
 * Aggregate consumption events into per-feature breakdown rows. The result is
 * sorted descending by creditsUsed and percentOfTotal sums to ~100 (rounding).
 */
export function buildFeatureBreakdown(events: ConsumptionEvent[]): FeatureUsageBreakdown[] {
  if (events.length === 0) {
    return [];
  }
  const totals = new Map<FeatureKey, { credits: number; count: number }>();
  let grandTotal = 0;
  for (const evt of events) {
    const prev = totals.get(evt.featureKey) ?? { credits: 0, count: 0 };
    prev.credits += evt.creditsConsumed;
    prev.count += 1;
    totals.set(evt.featureKey, prev);
    grandTotal += evt.creditsConsumed;
  }
  if (grandTotal === 0) {
    return [];
  }
  const rows: FeatureUsageBreakdown[] = [];
  for (const [featureKey, agg] of totals.entries()) {
    rows.push({
      featureKey,
      displayName: FEATURE_DISPLAY_NAMES[featureKey],
      creditsUsed: agg.credits,
      requestCount: agg.count,
      percentOfTotal: Math.round((agg.credits / grandTotal) * 100)
    });
  }
  rows.sort((a, b) => b.creditsUsed - a.creditsUsed);
  return rows;
}

/** Stable feature display name lookup for views that don't go through the breakdown. */
export function featureDisplayName(key: FeatureKey): string {
  return FEATURE_DISPLAY_NAMES[key];
}
